import { isEqual } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { useCaseTemplateVersion } from "src/cases/queries/templates";
import {
  CaseTemplateFormValues,
  transformTemplateToFormValues,
} from "src/cases/templates/caseTemplateFormUtils";
import {
  FlattenedStatusValue,
  flattenStatusValues,
} from "src/cases/templates/utils";
import { isStatusProperty } from "src/cases/utils";

export const useCaseTemplateVersionForm = (
  templateId: string,
  versionId: string,
) => {
  return useCaseTemplateVersion<CaseTemplateFormValues>(
    { templateId, versionId },
    {
      select: transformTemplateToFormValues,
    },
  );
};

export const useTemplateVersionStatusValues = () => {
  const { getValues } = useFormContext<CaseTemplateFormValues>();
  const values = getValues("attributes.case_properties");

  const statusValues = useMemo(() => {
    const statusProperty = values?.find(isStatusProperty);
    return flattenStatusValues(statusProperty);
  }, [values]);

  /**
   * Get a status value by its status and optional sub-status.
   */
  const getStatusValue = useCallback(
    (status: string, subStatus?: Nullable<string>) => {
      return statusValues.find((value) =>
        isEqual(
          {
            status,
            subStatus: subStatus ?? null,
          },
          {
            status: value.status,
            subStatus: value.subStatus ?? null,
          },
        ),
      );
    },
    [statusValues],
  );

  return { statusValues, getStatusValue } as const;
};

export const useCurrentStatusValue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status");
  const subStatus = searchParams.get("sub_status");

  const setCurrentStatusValue = (statusValue: FlattenedStatusValue) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      newParams.set("status", statusValue.status);
      if (statusValue.subStatus) {
        newParams.set("sub_status", statusValue.subStatus);
      } else {
        newParams.delete("sub_status");
      }

      return newParams;
    });
  };

  return [
    {
      status,
      subStatus,
    },
    setCurrentStatusValue,
  ] as const;
};

/**
 * Get case properties that can be used in automation rules (tags and enum types).
 */
export const useAutomationCaseProperties = () => {
  const properties = useWatch<
    CaseTemplateFormValues,
    "attributes.case_properties"
  >({ name: "attributes.case_properties" });

  return useMemo(
    () =>
      properties?.filter(
        (property) => property.type === "tags" || property.type === "enum",
      ),
    [properties],
  );
};
